import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const VoiceoverPlayer = ({ src, label }: { src: string; label: string }) => {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const pct = duration ? (time / duration) * 100 : 0;

  return (
    <div className="mb-8 flex items-center gap-4 rounded-2xl border border-border bg-card p-4">
      <audio ref={ref} src={src} preload="none" />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause audio overview" : "Play audio overview"}
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105"
      >
        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-px" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <div
          className="mt-2 h-1.5 cursor-pointer rounded-full bg-primary/15"
          onClick={(e) => {
            const a = ref.current;
            if (!a || !duration) return;
            const r = e.currentTarget.getBoundingClientRect();
            a.currentTime = ((e.clientX - r.left) / r.width) * duration;
          }}
        >
          <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="font-mono text-xs text-muted-foreground tabular-nums">
        {fmt(time)}
        {duration ? ` / ${fmt(duration)}` : ""}
      </span>
    </div>
  );
};
